import type { MouseEvent } from 'react';
import { Link, useNavigate } from 'react-router';
import type { OrderSummary } from '../../data/orders.ts';
import { formatIsoDate } from '../../domain/dates.ts';
import { equipmentLabel } from '../../domain/orders.ts';
import { StatusBadge } from '../../ui/StatusBadge.tsx';
import { CalendarIcon } from '../../ui/icons.tsx';
import { Assignee, QuoteBadge, WorkType } from './OrderCard.tsx';

interface OrderTableProps {
  orders: OrderSummary[];
  /** Personal administrativo: agrega la columna Técnico (el técnico solo ve sus propias órdenes). */
  staff: boolean;
  /** En la ficha del cliente el nombre sobra: la columna principal es el equipo. */
  hideCustomer?: boolean;
}

// Vista de tabla de la lista de órdenes (panel y ficha del cliente), solo en escritorio. Se adapta al ancho
// de la propia tabla, sin desplazamiento lateral:
// - Angosta: OS y Cliente; debajo del cliente, el equipo, la falla, el estado y el técnico (o el Tipo Garantía).
// - Desde 560 px de ancho de la tabla: el estado pasa a su propia columna.
// - Desde 900 px: una columna por dato.
// La OT interna va siempre debajo de la OS. Cada dato está una sola vez a la vista; lo apilado y las columnas se alternan con CSS (orders.css).
// El folio es el enlace a la orden; tocar cualquier otra parte de la fila también la abre.
export function OrderTable({ orders, staff, hideCustomer = false }: OrderTableProps) {
  const navigate = useNavigate();

  function openRow(event: MouseEvent<HTMLTableRowElement>, href: string) {
    // El enlace de la OS se abre solo (teclado, lector de pantalla, otra pestaña): la fila no lo repite.
    if (event.target instanceof Element && event.target.closest('a')) return;
    if (event.ctrlKey || event.metaKey) window.open(href, '_blank', 'noopener');
    else navigate(href);
  }

  return (
    <div className="order-table-wrap">
      <table className="order-table">
        <thead>
          <tr>
            <th scope="col">OS</th>
            <th scope="col" className="order-table-md-up">
              Estado
            </th>
            {/* Cada encabezado lleva la clase de sus celdas: el ancho de la columna depende de ambos. */}
            <th scope="col" className="order-table-main">
              {hideCustomer ? 'Equipo' : 'Cliente'}
            </th>
            {hideCustomer ? null : (
              <th scope="col" className="order-table-lg-only order-table-wrap-text">
                Equipo
              </th>
            )}
            <th scope="col" className="order-table-lg-only">
              Tipo Garantía
            </th>
            {staff ? (
              <th scope="col" className="order-table-lg-only">
                Técnico
              </th>
            ) : null}
            <th scope="col" className="order-table-lg-only">
              Solicitud
            </th>
          </tr>
        </thead>
        <tbody>
          {orders.map((order) => {
            const href = `/orders/${order.id}`;
            const equipment = equipmentLabel(order);
            const date = formatIsoDate(order.request_date);
            return (
              <tr
                key={order.id}
                className={`order-row order-row-${order.status}`}
                onClick={(event) => openRow(event, href)}
              >
                <td className="order-table-folio">
                  <Link to={href} className="order-table-link" aria-label={`OS ${order.folio}`}>
                    <span className="folio-number">{order.folio}</span>
                  </Link>
                  <span className="order-table-sub order-table-ot">OT {order.order_number}</span>
                  {order.has_quote ? <QuoteBadge className="order-table-sub order-table-ot" /> : null}
                  <span className="order-table-sub order-table-date order-table-stacked">
                    <CalendarIcon />
                    <span className="visually-hidden">Solicitud </span>
                    {date}
                  </span>
                </td>
                <td className="order-table-md-up">
                  <StatusBadge status={order.status} />
                </td>
                <td className="order-table-main">
                  <span className="order-table-title">{hideCustomer ? equipment : order.customer_name}</span>
                  {hideCustomer ? null : (
                    <span className="order-table-sub order-table-ellipsis order-table-stacked">{equipment}</span>
                  )}
                  {/* La falla reportada, debajo del equipo (con la columna Equipo, va en ella). */}
                  <span
                    className={`order-table-sub order-table-ellipsis${hideCustomer ? '' : ' order-table-stacked'}`}
                  >
                    {order.reported_fault}
                  </span>
                  {/* Lo mismo que el pie de la tarjeta: el técnico para el personal, el Tipo Garantía para el técnico. */}
                  <span className="order-table-meta order-table-stacked">
                    <span className="order-table-sm-only">
                      <StatusBadge status={order.status} />
                    </span>
                    {staff ? (
                      <Assignee name={order.assigned_name} className="order-table-icon-text" />
                    ) : (
                      <WorkType type={order.work_type} className="order-table-icon-text" />
                    )}
                  </span>
                </td>
                {hideCustomer ? null : (
                  <td className="order-table-lg-only order-table-wrap-text">
                    {equipment}
                    {/* En una línea: la falla no ensancha la columna (el texto completo, al pasar el cursor). */}
                    <span className="order-table-sub order-table-ellipsis" title={order.reported_fault}>
                      {order.reported_fault}
                    </span>
                  </td>
                )}
                <td className="order-table-lg-only">
                  <WorkType type={order.work_type} className="order-table-icon-text" />
                </td>
                {staff ? (
                  <td className="order-table-lg-only">
                    <Assignee name={order.assigned_name} className="order-table-icon-text" />
                  </td>
                ) : null}
                <td className="order-table-lg-only order-table-date">{date}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
